import validate from 'validate.js';
import debounce from 'lodash/debounce';
import API from '@utilities/api';
import parseJSON from '@utilities/parse-json';
import serializeObject from '@utilities/serialize-object';
import Events from '@utilities/events';
import errorHandler from '@utilities/errors/error-handler';
import customValidators from '@utilities/validation/validators';
import ButtonLoader from '@components/button-loader';
import apiErrorHandler from '@utilities/errors/api-error-handler';
import singleAsyncValidator from '@utilities/validation/helpers/single-async-validator';
import Component from '@utilities/store/lib/component';
import giftConstraints from '../../components/shipping-form/gift-constraints';

const JS_HOOK_DEFAULT_SUBMIT = '[type="submit"]:not(.u-sr-only)';
const ALERT_SELECTOR = '[js-hook-message]';
const FORM_SELECTOR = '.c-form';
const INPUT_TYPES =
  'input[name]:not(:disabled):not([type="hidden"]):not([data-ignored]), textarea[name]:not([data-ignored]), select[name]:not([data-ignored])';
const FORM_ITEM_CLASS = '.form__item';
const FORM_ITEM_WARN_CLASS = 'form__item--warn';
const FORM_ITEM_ERROR_CLASS = 'form__item--error';
const FORM_ITEM_ERROR_ARIA = 'aria-invalid';
const FORM_ITEM_DESCRIBE_ARIA = 'aria-describedby';
const FORM_ITEM_SUCCESS_CLASS = 'form__item--success';
const FORM_ITEM_ERROR_MESSAGE_HOOK = '.form__item-error:not([data-ignored])';
const REGEX_REMOVE_NEWLINES_AND_WHITESPACE = /[\n\r]+|[\s]{2,}/g;

class Form extends Component {
  constructor(element, store) {
    super({ store, element });

    this.form = element;
    this.endpoint = element.getAttribute('action');
    this.gtmObj = element.dataset.gtmObj;

    this.messageSelector = ALERT_SELECTOR;
    this.formSelector = FORM_SELECTOR;

    this.class = FORM_ITEM_CLASS;
    this.inputTypes = INPUT_TYPES;
    this.warnClass = FORM_ITEM_WARN_CLASS;
    this.errorClass = FORM_ITEM_ERROR_CLASS;
    this.errorAria = FORM_ITEM_ERROR_ARIA;
    this.describeAria = FORM_ITEM_DESCRIBE_ARIA;
    this.successClass = FORM_ITEM_SUCCESS_CLASS;
    this.message = FORM_ITEM_ERROR_MESSAGE_HOOK;

    // Constraints & Validation
    this.defaultValidators = this.getDefaultValidators();
    this.customValidationMessages = this.getCustomValidationMessages();
    this.constraints = {};
    this.customConstraints = {};

    // Buttons
    this.buttonLoaders = [];
    this.defaultSubmit = this.form.querySelector(JS_HOOK_DEFAULT_SUBMIT);

    this.initInputs();
    this.init();
    this.bindEvents();
  }

  init() {
    // Validators
    this.setInitialValidators();
    singleAsyncValidator();

    // Events
    const debounceSingleFieldValidation = debounce(
      (input) => this.singleFieldValidation(input),
      800,
    );
    this.changeListener = (input) => debounceSingleFieldValidation(input);
    this.keyupListener = (input) => this.singleFieldValidation(input);
  }

  initInputs() {
    this.inputs = [...this.form.querySelectorAll(this.inputTypes)];
  }

  updateInputs() {
    this.initInputs();
    this.bindChangeEvents();
  }

  bindEvents() {
    Events.$on('form::update-inputs', () => this.updateInputs());
  }

  set constraints(obj) {
    this._constraints = obj;
  }

  get constraints() {
    return this._constraints;
  }

  initButtonLoaders() {
    if (this.defaultSubmit) {
      this.addButtonLoader(this.defaultSubmit, false);
    }
  }

  hideButtonLoaders() {
    this.buttonLoaders.forEach((loader) => loader.hide());
    this.buttonLoaders = [];
  }

  /**
   * Changes the button loader
   * @param {HTMLElement} element - DOM element of the button
   * @param {Boolean} shouldAnimate - button should animate with a loader element
   */
  addButtonLoader(element, shouldAnimate) {
    if (!element) return;
    this.buttonLoaders.push(new ButtonLoader(element, shouldAnimate));
    this.buttonLoaders[this.buttonLoaders.length - 1].show();
  }

  getButtonElement(target) {
    if (!target) return null;
    if (target.tagName.toLowerCase() === 'form') {
      return this.form.querySelector('[type="submit"]');
    }
    if (target.tagName.toLowerCase() === 'button') {
      return target;
    }
    return null;
  }

  getDefaultValidators() {
    const { data, error } = Form.parseDefaults(this.form.dataset.defaultValidators);
    if (error) return new Error(`Form.js - getDefaultValidators - Error: ${error}`);
    return data;
  }

  getCustomValidationMessages() {
    const { data, error } = Form.parseDefaults(this.form.dataset.customValidationMessages);
    if (error) return new Error(`Form.js - getCustomValidationMessages - Error: ${error}`);
    return data;
  }

  /**
   * Parse an object containing defaults
   * @param {Object} defaults - defaults object
   */
  static parseDefaults(defaults) {
    const data = parseJSON(defaults, {});
    if (!data)
      return {
        error: "Couldn't parse defaults in Form.js - function parseDefaults",
      };
    return { data };
  }

  /**
   * Set initial validators on ValidateJS.
   * Add defaultValidator messages & custom validators for ean, zipcode etc
   */
  setInitialValidators() {
    if (!this.defaultValidators) return;

    // Init Default Validators
    Object.keys(this.defaultValidators).forEach((key) => {
      validate.validators[key].options = this.defaultValidators[key];
    });

    // Init Custom Validators
    Object.keys(customValidators).forEach((key) => {
      validate.validators[key] = customValidators[key];
    });
  }

  /**
   * Shows errors from backend/server in form per field or on top of form when it is a generic server error
   * @param {Object} data - the response data
   */
  processServerErrors(data) {
    if (data.fieldErrors?.length) {
      data.fieldErrors.forEach((error) => {
        if (Object.keys(error).length) {
          this.showErrors(error);
        }
      });
    }

    if (data.serverErrors?.length || data.errorMessage?.length) {
      apiErrorHandler(data, this.messageSelector);
    }
  }

  /**
   * Submit form success handler
   * @param {Object} data - data returned from API call
   */
  submitFormSuccess(data) {
    if (data.error) {
      if (typeof this.afterSubmitFormError === 'function') {
        this.afterSubmitFormError(data);
      } else {
        this.processServerErrors(data);
      }
    }
    // If extended form provides afterSubmitFormSuccess function, this will be called
    else if (typeof this.afterSubmitFormSuccess === 'function') {
      this.afterSubmitFormSuccess(data);
    }

    if (this.gtmObj) {
      Events.$trigger('gtm::push', {
        data: JSON.parse(this.gtmObj),
      });
    }

    if (data.redirectUrl) {
      window.location.href = data.redirectUrl;
    } else {
      this.hideButtonLoaders();
    }
  }

  submitFormError(error) {
    this.hideButtonLoaders();

    // If extended form provides simpleAfterSubmitFormError function, this will be called
    if (typeof this.simpleAfterSubmitFormError === 'function') {
      this.simpleAfterSubmitFormError();
    }

    errorHandler(error);
  }

  /**
   * Post form to API
   */
  preSubmitForm() {
    this.submitForm(this.serializeForm());
  }

  serializeForm() {
    return serializeObject(this.form);
  }

  submitForm(data) {
    API.post(this.endpoint, data)
      .then((response) => this.submitFormSuccess(response.data))
      .catch((error) => this.submitFormError(error));
  }

  /**
   * Form validation success handler
   *
   * @param event, submit event
   */
  validateFormSuccess(event) {
    if (event) {
      const target = this.getButtonElement(event.currentTarget);
      this.addButtonLoader(target, true);
    }
    this.preSubmitForm();
  }

  /**
   * Form validation error handler
   *
   * @param {Array} errors | errors returned from ValidateJS
   */
  validateFormError(errors) {
    if (errors instanceof Error) {
      // This means an exception was thrown from a validator
      apiErrorHandler(errors, this.messageSelector);
    } else {
      this.showErrors(errors);
    }
  }

  /**
   * Submit the shipping form
   * @param event, submit event
   */
  validateForm(event = null) {
    if (event) event.preventDefault();

    this.validate()
      .then(
        () => this.validateFormSuccess(event),
        (errors) => {
          const errorKeys = Object.keys(errors);

          // if there are errors without warning, fail validation
          const isAnyError = errorKeys.some((key) => {
            const { fatalError } = getErrorData(errors[key]);
            return fatalError;
          });

          if (isAnyError) {
            this.validateFormError(errors);
            return;
          }
          this.validateFormSuccess(event);
        },
      )
      .catch((error) => errorHandler(error));
  }

  /**
   * Validate a single field
   *
   * @param {HTMLElement} input | input that fired onChange event
   * @returns {Promise}
   */
  validateSingle(input, custom) {
    let inputValue = input.value;
    if (input.type === 'checkbox') inputValue = input.checked;
    let constraints;

    if (input.id === 'dwfrm_gift_firstName' || input.id === 'dwfrm_gift_lastName' || input.id === 'dwfrm_gift_email') {
      constraints = giftConstraints();;
    } else {
      constraints = custom === false ? this.constraints : this.customConstraints;
    }

    return validate.async.single(inputValue, constraints[input.name], {
      key: input.name,
    });
  }

  /**
   * Prevalidate filled fields
   */
  preValidateFields() {
    this.validateFields(this.inputs.filter((input) => input.value.length > 0));
  }

  /**
   * Validate fields from an array
   *
   * @param {HTMLElement[]} inputs | inputs array
   */
  validateFields(inputs) {
    ;[...inputs].forEach((input) => this.singleFieldValidation(input));
  }

  /**
   * Validate the form
   *
   * @returns {Promise}
   */
  validate() {
    const items = validate.collectFormValues(this.form);

    // create new constraints object
    // based on non hidden input fields
    const actualConstraints = {};

    Object.keys(items).forEach((item) => {
      const key = item;
      if (!this.constraints[key]) return;
      actualConstraints[key] = this.constraints[key];
    });

    return validate.async(items, actualConstraints, { fullMessages: false });
  }

  /**
   * Single field validation success handler
   *
   * @param {HTMLElement} input | input that fired onChange event
   */
  singleFieldValidationSuccess(input) {
    this.showSuccessForInput(input);
  }

  /**
   * Single field validation error handler
   *
   * @param {HTMLElement} input | input that fired onChange event
   * @param {Array} errors | errors returned from ValidateJS
   */
  singleFieldValidationError(input, errors) {
    this.showErrorsForInput(input, errors);
    return errors;
  }

  /**
   * Single field validation:
   * Can be called as onChange event or to validate a single input
   * @param {HTMLElement} input | input that fired onChange event
   */
  singleFieldValidation(input, custom = false) {
    if (Form.numberFieldContainsText(input)) {
      input.value = '';
    }

    return this.validateSingle(input, custom)
      .then(
        () => this.singleFieldValidationSuccess(input),
        (errors) => this.singleFieldValidationError(input, errors),
      )
      .catch((error) => apiErrorHandler(error));
  }

  /**
   * Checks if input type="number" fields contain text
   *
   * @param {HTMLElement} input | input element to check
   * @returns {Boolean} return true if number field contains text, false otherwise
   */
  static numberFieldContainsText(input) {
    if (input.getAttribute('type') === 'number') {
      return !/^\d+$/.test(input.value);
    }

    return false;
  }

  /**
   * Gather validation errors and show per input
   *
   * @param {Array} errors | errors returned from ValidateJS
   */
  showErrors(errors) {
    if (!errors) return;
    // const formItems = this.form.querySelectorAll(this.inputTypes)

    let scrolledToError = false;
    ;[...this.inputs].forEach((input) => {
      if (!scrolledToError && errors[input.name]) {
        const { fatalError } = getErrorData(errors[input.name]);

        if (fatalError) {
          input.focus();
          Events.$trigger('scroll-to::scroll', {
            data: {
              target: input.offsetParent !== null ? input : input.closest(FORM_ITEM_CLASS),
              offset: 180,
            },
          });
          scrolledToError = true;
        }
      }
      this.showErrorsForInput(input, errors[input.name]);
    });
  }

  getErrorContainer(input) {
    if (input.name === 'brandCode') return false;
    const formItem = input.closest(this.class);

    return formItem?.querySelector(this.message);
  }

  /**
   * Display validation error on a specific field
   *
   * @param {HTMLElement} input | input to show error on
   * @param {Array} errors | errors returned from ValidateJS or Backend
   */
  showErrorsForInput(input, errors) {
    if (input.hasAttribute('data-ignored')) return; // Allows us to ignore certain inputs. https://validatejs.org/#utilities-collect-form-values doesn't seem to work.

    const formItem = input.closest(this.class);
    const formItemErrorContainer = this.getErrorContainer(input);

    if (!formItemErrorContainer) {
      return;
    }

    // reset form input state
    this.resetFormItem(formItem, input);
    if (errors) {
      const { warning, fatalError, errorMessage } = getErrorData(errors);
      input.setAttribute(this.errorAria, true);
      input.setAttribute(this.describeAria, formItemErrorContainer.id);
      formItem.classList.add(!fatalError && warning ? this.warnClass : this.errorClass);
      formItemErrorContainer.textContent = errorMessage;
      formItemErrorContainer.removeAttribute('aria-hidden');
      const labelContent =
        document
          .querySelector(`label[for="${input.id}"]`)
          ?.textContent.replace(REGEX_REMOVE_NEWLINES_AND_WHITESPACE, ' ')
          .trim() || '';
      Events.$trigger('gtm::errorEvent', {
        data: {
          errorMessage,
          errorId: labelContent,
        },
      });
    } else if (input.value.length > 0) {
      formItem.classList.add(this.successClass);
    }
  }

  showSuccessForInput(input) {
    const formItem = input.closest(this.class);
    this.resetFormItem(formItem, input);

    if (input.value.length > 0) {
      formItem.classList.add(this.successClass);
    }
  }

  /**
   * Remove error classes and any old error message
   * @param {HTMLElement} formItem | input parent that holds error/success classes
   * @param {HTMLElement} input | input element
   */
  resetFormItem(formItem, input) {
    const formItemErrorContainer = this.getErrorContainer(input);
    input.removeAttribute(this.errorAria);
    input.removeAttribute(this.describeAria);

    if (formItemErrorContainer) {
      formItemErrorContainer.setAttribute('aria-hidden', 'true');
      formItemErrorContainer.textContent = '';
    }

    if (formItem) {
      formItem.classList.remove(this.errorClass);
      formItem.classList.remove(this.warnClass);
      formItem.classList.remove(this.successClass);
    }
  }

  bindSubmitEvents() {
    // TODO: For debug of forms, keep for now
    // this.form.addEventListener('submit', event => {
    //   event.preventDefault(event);
    //   this.submitForm();
    // });
    this.form.addEventListener('submit', (event) => this.validateForm(event));
  }

  /**
   * Bind change events of input elements
   */
  bindChangeEvents() {
    this.inputs.map((input) => {
      const eventName =
        input.tagName === 'SELECT' || input.type === 'checkbox' || input.type === 'radio'
          ? 'change'
          : 'keyup';
      input.addEventListener(eventName, (event) => {
        if (event.keyCode !== 9 && eventName === 'change') {
          this.changeListener(input);
        } else if (event.keyCode !== 9) {
          this.keyupListener(input);
        }
      });
      input.addEventListener('paste', () => this.changeListener(input));
    });
    this.hideButtonLoaders();
  }
}

function getErrorMessage(errorData) {
  if (errorData) {
    if (errorData.message) {
      return errorData.message;
    }
    return errorData;
  }
  return false;
}

function getErrorData(errors) {
  const errorList = errors.constructor === Array ? [...errors] : [errors];
  const fatalError = errorList.find((error) => !error.warn);
  const warning = errorList.find((error) => error.warn);

  return {
    fatalError,
    warning,
    errorMessage: getErrorMessage(fatalError || warning),
  };
}

export default Form;
